import { Inject } from '@odinjs/odin';
import { DatePickerAPI, DatePickerDay, DeckAPI, FilterAPI, SegmentedControlAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnChange, OnClick, OnMount, OnNavigate, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableNavigateEvent, ChangeEvent, ClickEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';

import { FragmentNames, PhysicianScheduleViewerAliases } from '../../../codegen';
import { Endpoint } from '../../../constants/endpoints';
import { ApplicationRoute } from '../../../constants/routes';
import BaseController from '../../BaseController';
import { SegmentControlResponse } from './SegmentControlResponse';

interface TypeParams {
  establishmentCode?: number;
  type?: string;
}

interface FilterParams {
  establishments?: Array<number>;
  finalDate: string;
  initialDate: string;
  types?: Array<string>;
}

interface SegmentedValues {
  description?: string
  scheduleType?: string,
  statusItem?: string,
}

interface ScheduleValues {
  establishmentName?: string,
  personName?: string,
  scheduleDate?: string,
  scheduleSequence?: number,
  scheduleStartTime?: string,
  scheduleStatusCode?: number,
  scheduleStatusDescription?: string,
  scheduleType?: string,
  scheduleTypeDescription?: string
}

enum ScheduleStatus {
  AVAILABLE = 1,
  BLOCKED = 50,
  CANCELED = 15,
  BUSY = 100,
  LOST = 20,
}

@Controller({ fragment: FragmentNames.PHYSICIAN_SCHEDULE_VIEWER })
class PhysicianScheduleViewerController extends BaseController {

  @Inject(PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARD_LIST_DECK)
    physicanScheduleDeckApi!: DeckAPI;

  @Inject(PhysicianScheduleViewerAliases.PHYSISCIAN_SCHEDULE_FILTER)
    physicianFilterApi!: FilterAPI;

  @Inject(PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARROSEL_SEGMENTED_CONTROL)
    physicianScheduleSegmentControlApi!: SegmentedControlAPI;

  @Inject(PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_DATE_PICKER)
    datePickerPhysicianScheduleApi!: DatePickerAPI;

  @Inject(PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARD_LIST_TITLE)
    cardListTitleApi!: TextAPI;

  internalNavigation!: boolean;

  handleSegmentedControlData(data: Record<string, ScheduleValues>[]): ScheduleValues[] {
    const scheduled = data.filter(schedule =>
      schedule.scheduleStatusCode !== Number(ScheduleStatus.AVAILABLE) &&
      schedule.scheduleStatusCode !== Number(ScheduleStatus.BLOCKED) &&
      schedule.scheduleStatusCode !== Number(ScheduleStatus.CANCELED) &&
      schedule.scheduleStatusCode !== Number(ScheduleStatus.LOST)
    );
    const available = data.filter(schedule => schedule.scheduleStatusCode === Number(ScheduleStatus.AVAILABLE));

    const distinctScheduleType = [...new Set(scheduled.map(item => item.scheduleTypeDescription))] as [];
    const distinctEstablishmentName = [...new Set(scheduled.map(item => item.establishmentName))] as [];

    const segmentedValues: SegmentedValues[] = distinctScheduleType.map(type => ({
      description: `${scheduled.filter(schedule => schedule.scheduleTypeDescription === type).length} ${MessageService.translate('label.appointments-text')}`,
      scheduleType: type,
      statusItem: 'scheduleTypeDescription',
    }));

    distinctEstablishmentName.map(establishment => {
      segmentedValues.push({
        description: `${scheduled.filter(schedule => schedule.establishmentName === establishment).length} ${MessageService.translate('label.appointments-text')}`,
        scheduleType: establishment,
        statusItem: 'establishmentName',
      });
    });

    if (available.length > 0) {
      segmentedValues.push({
        description: `${available.length} ${MessageService.translate('label.appointments-text')}`,
        scheduleType: MessageService.translate('label.available-appointments'),
        statusItem: 'available',
      });
    }

    return segmentedValues;
  }

  validateScheduleExists(filteredSchedules: Record<string, ScheduleValues>[], schedule: ScheduleValues): boolean {
    return filteredSchedules.some(i => i.scheduleSequence === schedule.scheduleSequence);
  }

  handleChangeSegmentControl(segmentedResponse: Record<string, Value>[]): void {
    if (!segmentedResponse) return;

    const schedules = this.context.get('schedules') as Record<string, ScheduleValues>[];
    const filteredSchedules = [] as Record<string, ScheduleValues>[];

    segmentedResponse.forEach(card => {
      if (card.statusItem === 'available') {
        schedules.forEach((schedule) => {
          if (!this.validateScheduleExists(filteredSchedules, schedule)) {
            if (schedule.scheduleStatusCode === Number(ScheduleStatus.AVAILABLE)) {
              filteredSchedules.push(schedule);
            }
          }
        })
      }

      if (card.statusItem === 'establishmentName') {
        schedules.forEach((schedule) => {
          if (!this.validateScheduleExists(filteredSchedules, schedule)) {
            if (schedule.establishmentName === card.scheduleType && this.showAppointmentInFilter(schedule.scheduleStatusCode)) {
              filteredSchedules.push(schedule);
            }
          }
        })
      }

      if (card.statusItem === 'scheduleTypeDescription') {
        schedules.forEach((schedule) => {
          if (!this.validateScheduleExists(filteredSchedules, schedule)) {
            if (schedule.scheduleTypeDescription === card.scheduleType && this.showAppointmentInFilter(schedule.scheduleStatusCode)) {
              filteredSchedules.push(schedule);
            }
          }
        })
      }
    })

    if (segmentedResponse.length === 0) {
      schedules.forEach(schedule => filteredSchedules.push(schedule));
    }

    const sortedSchedules = filteredSchedules.sort((a, b) => (moment(String(a.scheduleDate)).diff(moment(String((b.scheduleDate))))));
    this.physicanScheduleDeckApi.setData(sortedSchedules);
  }

  showAppointmentInFilter(scheduleStatusCode: ScheduleValues): boolean {
    return  scheduleStatusCode !== Number(ScheduleStatus.AVAILABLE) &&
            scheduleStatusCode !== Number(ScheduleStatus.BLOCKED) &&
            scheduleStatusCode !== Number(ScheduleStatus.CANCELED) &&
            scheduleStatusCode !== Number(ScheduleStatus.LOST);
  }

  handleSchedules(body: Record<string, FilterParams>, params: Record<string, Value>): void {
    const { physicanScheduleDeckApi, physicianScheduleSegmentControlApi } = this;
    const restoredSegmentedControlValue = physicianScheduleSegmentControlApi.getValue() as Record<string, Value>[];
    physicanScheduleDeckApi.setLoading(true);

    HttpService.post(Endpoint.PHYSICIAN_SCHEDULE, params, { body })
      .then((response) => {
        const schedules = response.body as Record<string, ScheduleValues>[];

        if (schedules) {
          this.context.set({ schedules });
          physicianScheduleSegmentControlApi.setData(this.handleSegmentedControlData(schedules) as Record<string, Value>[]);
          physicanScheduleDeckApi.setData(schedules);
          if (restoredSegmentedControlValue) {
            this.handleChangeSegmentControl(restoredSegmentedControlValue);
          }
        }
      }).finally(() => physicanScheduleDeckApi.setLoading(false));
  }

  handleOccupationSchedule(body: Record<string, Value>, params: Record<string, Value>): void {
    const { datePickerPhysicianScheduleApi } = this;

    HttpService.post(Endpoint.PHYSICIAN_SCHEDULE_OCCUPATION, params, { body })
      .then((response) => {
        const schedulesOccupation = response.body as Record<string, Value>[];

        if (schedulesOccupation) {
          const occupationSchedule = [] as DatePickerDay[];
          schedulesOccupation.forEach(occupation => {
            if (Number(occupation.scheduleTotal) > 0) {
              occupationSchedule.push({
                active: true,
                busyPercent: ScheduleStatus.BUSY,
                date: moment(String(occupation.scheduleDate)),
                events: [],
              })
            }
          })
          datePickerPhysicianScheduleApi.setData(occupationSchedule);
        }
      })
  }

  @OnMount({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_DATE_PICKER })
  onDatePickerMount(event: MountEvent): void {
    const datePicker = event.target as DatePickerAPI;
    const restored = datePicker.restoreState(true);
    if (restored) {
      const datePickerValue = datePicker.getData() as unknown as string;
      datePicker.setValue(datePickerValue);
    }
  }

  @OnUnmount({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_DATE_PICKER })
  OnDatePickerUnmount(event: MountEvent): void {
    const datePicker = event.target as DatePickerAPI;
    if (this.internalNavigation) {
      const record = datePicker.getData();

      datePicker.setData(record);
      datePicker.persistState();
    } else {
      datePicker.clearPersistedState();
    }
  }

  @OnChange({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_DATE_PICKER })
  onChangeDatePicker(event: ChangeEvent<DatePickerAPI>): void {
    this.datePickerPhysicianScheduleApi.setValue(String(event.value));
    this.internalNavigation = true;

    const params = {
      initialDate: moment(String(event.value)).utc().format(ISO_DATE_FORMAT),
    };

    this.physicianFilterApi.setData(params);
  }

  @OnNavigate({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_DATE_PICKER })
  onScheduleNavigate(event: AbortableNavigateEvent<string>): void {
    if (event.to) {
      const finalDate = moment(String(event.to)).utc().format(ISO_DATE_FORMAT);
      const initialDate = moment(String(event.from)).utc().format(ISO_DATE_FORMAT);

      const params = {
        limit: 100,
        page: 0,
      };

      const OccupationBody = {
        finalDate,
        initialDate,
      }

      this.handleOccupationSchedule(OccupationBody, params);
    }
  }

  @OnMount({ target: PhysicianScheduleViewerAliases.PHYSISCIAN_SCHEDULE_FILTER })
  onFilterMount(event: MountEvent): void {
    const currentDate = moment();

    const formatedCurrentDate = currentDate.format(ISO_DATE_FORMAT);

    const record = {
      initialDate: formatedCurrentDate,
    };

    const filter = event.target as FilterAPI;
    const restored = filter.restoreState(true);

    if (restored) {
      Object.assign(record, filter.getRecord());
    }

    filter.setData(record);
  }

  @OnUnmount({ target: PhysicianScheduleViewerAliases.PHYSISCIAN_SCHEDULE_FILTER })
  OnFilterUnmount(event: MountEvent): void {
    const filter = event.target as FilterAPI;

    if (this.internalNavigation) {
      const record = filter.getRecord() as Record<string, FilterParams>;

      filter.setData(record);
      filter.persistState();
    } else {
      filter.clearPersistedState();
    }
  }

  @OnBeforeSubmit({ target: PhysicianScheduleViewerAliases.PHYSISCIAN_SCHEDULE_FILTER })
  onBeforeSubmit(event: SubmitEvent): void {
    const title = moment(event.record.initialDate).format('dddd ll');
    this.cardListTitleApi.setValue(title);
    this.datePickerPhysicianScheduleApi.setValue(event.record.initialDate);

    const params = {
      limit: 100,
      page: 0,
    };

    const currentDate = moment(event.record.initialDate);
    const weekStart = currentDate.clone().startOf('week').format(ISO_DATE_FORMAT);
    const weekEnd = currentDate.clone().endOf('week').format(ISO_DATE_FORMAT);

    const baseBody = {
      establishmentCodes: event.record?.establishments?.map((establishment: TypeParams) => establishment.establishmentCode),
      scheduleTypes: event.record?.types?.map((scheduleType: TypeParams) => scheduleType.type),
    }

    const schedulesBody = {
      finalDate: event.record.initialDate,
      initialDate: event.record.initialDate,
    };

    const OccupationBody = {
      finalDate: weekEnd,
      initialDate: weekStart,
    }

    this.handleSchedules(Object.assign(baseBody, schedulesBody), params);
    this.handleOccupationSchedule(Object.assign(baseBody, OccupationBody), params);
  }

  @OnMount({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARROSEL_SEGMENTED_CONTROL })
  onSegmentedControlMount(event: MountEvent): void {
    const segmentedControl = event.target as SegmentedControlAPI;

    const restored = segmentedControl.restoreState(true);
    if (restored) {
      const segmentedValue = segmentedControl.getValue();
      segmentedControl.setValue(segmentedValue);
    }
  }

  @OnUnmount({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARROSEL_SEGMENTED_CONTROL })
  onSegmentedControlUnmount(event: MountEvent): void {
    const segmentedControl = event.target as SegmentedControlAPI;
    if (this.internalNavigation) {
      const record = segmentedControl.getValue() as Record<string, Value>[];

      segmentedControl.setValue(record);
      segmentedControl.persistState();
    } else {
      segmentedControl.clearPersistedState();
    }
  }

  @OnChange({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARROSEL_SEGMENTED_CONTROL })
  onSegmentedControlChange(response: SegmentControlResponse): void {
    this.handleChangeSegmentControl(response.value as Record<string, Value>[])
  }

  @OnClick({ target: PhysicianScheduleViewerAliases.PHYSICIAN_SCHEDULE_CARD_LIST_DECK })
  onDeckClick(event: ClickEvent): void {

    const { establishmentCode, scheduleSequence, scheduleType, scheduleStatusCode } = event.value;
    this.internalNavigation = true;

    if (scheduleStatusCode !== ScheduleStatus.AVAILABLE && scheduleStatusCode !== ScheduleStatus.BLOCKED) {
      applicationContext.redirect(ApplicationRoute.PHYSICIAN_SCHEDULE_APPOINTMENTS_DETAIL, { establishmentCode, scheduleSequence, scheduleType });
    }
    if (scheduleStatusCode === ScheduleStatus.AVAILABLE || scheduleStatusCode === ScheduleStatus.BLOCKED) {
      applicationContext.redirect(ApplicationRoute.PHYSICIAN_SCHEDULE_BLOCK_INDIVIDUAL, {establishmentCode, scheduleSequence, scheduleType });
    }
  }
}

export default PhysicianScheduleViewerController;
